import ApprovalIcon from "@Assets/icons/svg/ReviewTickMark.svg";
import InProgressIcon from "@Assets/icons/svg/ReviewInProgressIcon.svg";
import PendingIcon from "@Assets/icons/svg/ReviewPendingIcon.svg";
import RevokedIcon from "@Assets/icons/svg/ReviewRevokeIcon.svg";
import RightArrow from "@Assets/icons/svg/lineWithRightArrow.svg";
import LeftArrow from "@Assets/icons/svg/lineWithLeftArrow.svg";
import BypassLevel from "@Assets/icons/svg/BypassLevel.svg";
import { getApprovalStagesToShow } from "./ReviewsHelper";
import type { ApprovalStagesToShow, ReviewApprovalStage } from "./ReviewsHelper";

type ApprovalStageToShow = ApprovalStagesToShow["approvalStagesToShow"][number];

type StageClearanceStatus = ReviewApprovalStage["clearanceStatus"];

interface StageDisplayConfig {
  icon: string;
  textStyle: { color: string };
  /** Absent on the `revoked` entry — see the KNOWN BUG note at the destructuring site. */
  parentClass?: string;
}

interface ReviewStageTransitionChartProps {
  readonly approvalStages: ReviewApprovalStage[];
  readonly isRollback?: boolean;
  readonly skipReviewTillLevel?: number | null;
  readonly rollbackReviewToLevel?: number | null;
}

function ReviewStageTransitionChart({
  approvalStages,
  isRollback = false,
  skipReviewTillLevel,
  rollbackReviewToLevel,
}: ReviewStageTransitionChartProps) {
  const { showStartLine, showEndLine, approvalStagesToShow, isReviewInitiated, willReviewClose } =
    getApprovalStagesToShow(approvalStages, isRollback, skipReviewTillLevel, rollbackReviewToLevel);

  function getTargetIndex(approvalStagesToShow: ApprovalStagesToShow["approvalStagesToShow"]) {
    if (isRollback) {
      return -1;
    }

    const approvalStageInProgressIndex = approvalStagesToShow.findIndex(
      (record) => record.clearanceStatus === "in_progress",
    );

    if (approvalStageInProgressIndex === -1 && !isReviewInitiated) {
      return 0;
    }

    if (approvalStageInProgressIndex !== -1) {
      return approvalStageInProgressIndex + 1;
    }

    return -1;
  }

  const targetIndex = getTargetIndex(approvalStagesToShow);

  const mapper = (rollbackTo: boolean, index: number): Record<StageClearanceStatus, StageDisplayConfig> => ({
    approved: {
      icon: ApprovalIcon,
      textStyle: { color: "#666A84" },
      parentClass: isRollback && rollbackTo ? "" : "opacity-50",
    },
    in_progress: {
      icon: InProgressIcon,
      textStyle: { color: "#192048" },
      parentClass: "",
    },
    pending: {
      icon: PendingIcon,
      textStyle: index === targetIndex ? { color: "#192048" } : { color: "#666A84" },
      parentClass: index === targetIndex ? "opacity-100" : "opacity-50",
    },
    revoked: {
      icon: RevokedIcon,
      textStyle: { color: "#192048" },
    },
    bypassed: {
      icon: BypassLevel,
      textStyle: { color: "#666A84" },
      parentClass: isRollback && rollbackTo ? "" : "opacity-50",
    },
  });

  const getConnectorType = ({
    rollbackTo,
    clearanceStatus,
    index,
  }: {
    rollbackTo: boolean;
    clearanceStatus: StageClearanceStatus;
    index: number;
    /** Passed by the caller but never read here. */
    rollbackReviewToLevel?: number | null;
  }) => {
    // The last stage never draws a connector; before it, the arrow shape follows the direction of
    // travel (forward for a live review, backward for a rollback).
    if (index === approvalStagesToShow.length - 1) {
      return {
        showLeftArrow: false,
        showRightArrow: false,
        showStandardConnector: false,
      };
    }

    if (!isRollback) {
      return {
        showLeftArrow: false,
        showRightArrow: ["in_progress"].includes(clearanceStatus),
        showStandardConnector: ["approved", "pending", "bypassed"].includes(clearanceStatus),
      };
    }

    return {
      showLeftArrow: rollbackTo && ["approved", "bypassed"].includes(clearanceStatus),
      showRightArrow: false,
      showStandardConnector: !rollbackTo,
    };
  };

  return (
    <div className="flex flex-row items-center gap-6">
      {!isReviewInitiated ? (
        <div className="flex flex-row gap-4">
          <img src={InProgressIcon} />
          <img src={RightArrow} />
        </div>
      ) : undefined}
      {isReviewInitiated && showStartLine && (
        <p className=" w-16 border-b-2 border-dashed border-system_colour_45 opacity-50" />
      )}
      {approvalStagesToShow.map((stage: ApprovalStageToShow, index: number) => {
        const { clearanceStatus, serial, rollbackTo } = stage;
        // KNOWN BUG, preserved verbatim: the `revoked` entry of `mapper` has no `parentClass`, so a
        // revoked stage renders `className="flex flex-row items-center gap-2 undefined"`. Does the
        // server read it? No — display-only. Fix: add `parentClass: ""` to the revoked entry (that
        // would change the emitted object, so it is left alone).
        const { parentClass, icon, textStyle } = mapper(rollbackTo, index)[clearanceStatus];
        const { showLeftArrow, showRightArrow, showStandardConnector } = getConnectorType({
          rollbackTo,
          clearanceStatus,
          index,
          rollbackReviewToLevel,
        });

        return (
          <div key={index} className="flex flex-row items-center gap-4">
            <div className={`flex flex-row items-center gap-2 ${parentClass}`}>
              <img src={icon} />
              <p style={textStyle} className="text-base font-normal">{`Level ${serial}`}</p>
            </div>
            {showLeftArrow && <img src={LeftArrow} />}
            {showRightArrow && <img src={RightArrow} />}
            {showStandardConnector && <p className="w-16 border-b-2 border-system_colour_45 opacity-50" />}
          </div>
        );
      })}
      {isReviewInitiated && showEndLine && (
        <p className=" w-16 border-b-2 border-dashed border-system_colour_45 opacity-50" />
      )}
      {willReviewClose ? (
        <div className="flex flex-row gap-4">
          <img src={RightArrow} />
          <img src={ApprovalIcon} />
        </div>
      ) : undefined}
      {showEndLine && <p className="border-b-2 border-dashed border-system_colour_45 opacity-50 my-auto" />}
    </div>
  );
}

export default ReviewStageTransitionChart;
